import React, { useState, useEffect, useRef } from 'react';
import { useGeolocation } from '@/hooks/useGeolocation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Navigation, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export const RiderTracking = ({ orderId, onTrackingStart, onTrackingStop }) => {
  const {
    location,
    error,
    permission,
    isTracking,
    isGeolocationAvailable,
    isSecureContext,
    requestLocation,
    startTracking,
    stopTracking
  } = useGeolocation();

  const [isActive, setIsActive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  // Enviar ubicación al backend cada 10 segundos
  useEffect(() => {
    if (isActive && location && orderId) {
      const sendLocation = async () => {
        try {
          await axios.post(`/tracking/update-location/${orderId}`, {
            latitude: location.latitude,
            longitude: location.longitude,
            speed: location.speed,
            heading: location.heading,
            timestamp: new Date().toISOString()
          });
          setLastUpdate(new Date());
        } catch (error) {
          console.error('Error enviando ubicación:', error);
        }
      };

      // Enviar inmediatamente y luego cada 10 segundos
      sendLocation();
      intervalRef.current = setInterval(sendLocation, 10000);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [isActive, location, orderId]);

  const handleStartTracking = async () => {
    if (!orderId) {
      toast.error('No hay pedido asignado');
      return;
    }

    try {
      // Solicitar ubicación primero
      await requestLocation();
      
      // Iniciar tracking en el backend
      await axios.post(`/tracking/start/${orderId}`);
      
      // Iniciar tracking GPS continuo
      startTracking();
      setIsActive(true);
      
      toast.success('Tracking iniciado correctamente');
      if (onTrackingStart) onTrackingStart();
    } catch (error) {
      console.error('Error iniciando tracking:', error);
      toast.error('No se pudo iniciar el tracking: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStopTracking = async () => {
    try {
      if (orderId) {
        await axios.post(`/tracking/stop/${orderId}`);
      }
      
      stopTracking();
      setIsActive(false);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      toast.success('Tracking detenido');
      if (onTrackingStop) onTrackingStop();
    } catch (error) {
      console.error('Error deteniendo tracking:', error);
    }
  };

  // Verificaciones de disponibilidad
  if (!isGeolocationAvailable) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Tu navegador no soporta Geolocation. Por favor actualiza tu navegador.
        </AlertDescription>
      </Alert>
    );
  }

  if (!isSecureContext) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Geolocation requiere HTTPS. La aplicación debe estar en un dominio seguro.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Navigation className="w-5 h-5" />
          Tracking en Tiempo Real
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Estado del GPS */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-medium">Estado del GPS:</span>
          </div>
          <div className="flex items-center gap-2">
            {permission === 'granted' && location ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-600">Activo</span>
              </>
            ) : permission === 'denied' ? (
              <>
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-600">Denegado</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-yellow-600">Sin permisos</span>
              </>
            )}
          </div>
        </div>

        {/* Ubicación actual */}
        {location && (
          <div className="p-3 bg-blue-50 rounded-lg space-y-2">
            <p className="text-xs font-medium text-blue-900">Ubicación Actual:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
              <div>
                <span className="font-medium">Lat:</span> {location.latitude.toFixed(6)}
              </div>
              <div>
                <span className="font-medium">Lng:</span> {location.longitude.toFixed(6)}
              </div>
              {location.accuracy && (
                <div className="col-span-2">
                  <span className="font-medium">Precisión:</span> {Math.round(location.accuracy)}m
                </div>
              )}
            </div>
          </div>
        )}

        {/* Estado del tracking */}
        {isActive && lastUpdate && (
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Clock className="w-3 h-3" />
            <span>Última actualización: {lastUpdate.toLocaleTimeString()}</span>
          </div>
        )}

        {/* Errores */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        {/* Instrucciones para permisos denegados */}
        {permission === 'denied' && (
          <Alert>
            <AlertDescription className="text-sm">
              <p className="font-medium mb-2">Para habilitar el GPS:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Chrome/Edge: Click en el icono de candado → Permisos del sitio → Ubicación → Permitir</li>
                <li>Safari: Configuración → Safari → Ajustes para sitios web → Ubicación</li>
                <li>Firefox: Click en el icono de candado → Permisos → Ubicación → Permitir</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Botones de control */}
        <div className="flex gap-2">
          {!isActive ? (
            <Button
              onClick={handleStartTracking}
              disabled={permission === 'denied'}
              className="w-full bg-[#0F7A5B] hover:bg-[#0d6549]"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Iniciar Tracking
            </Button>
          ) : (
            <Button
              onClick={handleStopTracking}
              variant="destructive"
              className="w-full"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Detener Tracking
            </Button>
          )}
        </div>

        {/* Indicador visual de tracking activo */}
        {isActive && (
          <div className="flex items-center justify-center gap-2 p-2 bg-green-50 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-green-700">Tracking Activo</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};